import { Link, NavLink } from "react-router-dom";
import { Heart, User, Menu, X } from "lucide-react";
import { useState } from "react";
import { cn } from "@/utils/cn";
import { useFavoritesStore } from "@/stores/favorites";
import { Logo } from "./Logo";

const navigation = [
  { name: "Home", href: "/" },
  { name: "Vehicles", href: "/vehicles" },
  { name: "Brands", href: "/brands" },
  { name: "Shop", href: "/shop" },
];

function FavoritesCount() {
  const { favorites } = useFavoritesStore();
  if (favorites.length === 0) return null;
  return (
    <span className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center bg-red-500 text-white text-xs font-medium rounded-full">
      {favorites.length > 9 ? "9+" : favorites.length}
    </span>
  );
}

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-line-gray">
      <nav className="max-w-7xl mx-auto px-6 lg:px-8" aria-label="Top">
        {/* Mobile/tablet: flex layout. Desktop (lg+): 3-col grid with centered nav. */}
        <div className="flex items-center justify-between h-20 lg:grid lg:grid-cols-3 lg:items-center">
          {/* Logo: img + two-line wordmark (match Bespokian Travel DOM) */}
          <Logo />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-center gap-6">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    "font-headline text-sm tracking-wide transition-colors",
                    isActive
                      ? "text-navy-900"
                      : "text-muted-navy hover:text-navy-900",
                  )
                }
              >
                {item.name}
              </NavLink>
            ))}
          </div>

          {/* Right side: actions (desktop) + mobile menu button */}
          <div className="flex items-center justify-end gap-6 lg:col-start-3">
            {/* Actions (match Travel: hidden sm:flex, same link classes) */}
            <div className="hidden sm:flex items-center gap-6">
              <Link
                to="/favorites"
                className="relative flex items-center gap-2 text-muted-navy text-sm tracking-wide hover:text-navy-900 transition-colors"
                title="My Favorites"
              >
                <Heart className="h-5 w-5" />
                <FavoritesCount />
              </Link>
              <Link
                to="/account"
                className="flex items-center gap-2 text-muted-navy text-sm tracking-wide hover:text-navy-900 transition-colors"
                title="My Account"
              >
                <User className="h-5 w-5" />
                Sign In
              </Link>
              <Link to="/vehicles" className="btn btn-primary">
                Browse Vehicles
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              type="button"
              className="lg:hidden text-muted-navy hover:text-navy-900 transition-colors p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-line-gray">
            <div className="flex flex-col gap-4">
              {navigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                    "font-headline text-base tracking-wide",
                    isActive
                      ? "text-navy-900"
                      : "text-muted-navy hover:text-navy-900",
                    )
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </NavLink>
              ))}
              <div className="border-t border-line-gray pt-4 mt-2 flex gap-4">
                <NavLink
                  to="/favorites"
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2 font-headline text-base tracking-wide",
                      isActive
                        ? "text-navy-900"
                        : "text-muted-navy hover:text-navy-900",
                    )
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Heart className="h-5 w-5" />
                  Favorites
                </NavLink>
                <NavLink
                  to="/account"
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2 font-headline text-base tracking-wide",
                      isActive
                        ? "text-navy-900"
                        : "text-muted-navy hover:text-navy-900",
                    )
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User className="h-5 w-5" />
                  Account
                </NavLink>
              </div>
              <Link
                to="/vehicles"
                className="btn btn-primary w-full"
                onClick={() => setMobileMenuOpen(false)}
              >
                Browse Vehicles
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
